import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, TagIcon, PencilIcon, TrashIcon, EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface Offer {
  id: string;
  name: string;
  offerType: string;
  inactive: boolean;
  slaCategory: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  User_Offer_createdByIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    displayName: string;
  };
}

interface OfferResponse {
  offers: Offer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const OfferList: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterOfferType = searchParams.get('offerType') ?? '';
  const filterStatus = searchParams.get('inactive') ?? '';
  const filterCreatedBy = searchParams.get('createdById') ?? '';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === appliedSearch) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, appliedSearch, patchSearchParams]);

  const fetchOffers = async (): Promise<OfferResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: pageSize.toString(),
    });
    if (appliedSearch) params.set('search', appliedSearch);
    if (filterName) params.set('name', filterName);
    if (filterOfferType) params.set('offerType', filterOfferType);
    if (filterStatus) params.set('inactive', filterStatus);
    if (filterCreatedBy) params.set('createdById', filterCreatedBy);

    const response = await api.get(`/offers?${params.toString()}`);
    return response.data;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['offers', page, pageSize, appliedSearch, filterName, filterOfferType, filterStatus, filterCreatedBy],
    queryFn: fetchOffers,
  });

  const { data: options } = useQuery({
    queryKey: ['offer-options'],
    queryFn: async () => {
      const response = await api.get('/offers/options');
      return response.data;
    },
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-offer-filter'],
    queryFn: async () => {
      const response = await api.get('/users?limit=200&role=agent,manager,admin,super_admin');
      return response.data;
    },
  });

  const offerTypeOptions = useMemo(
    () =>
      (options?.offerTypes || []).map((type: string) => ({
        value: type,
        label: type.replace('_', ' '),
      })),
    [options]
  );

  const createdByOptions = useMemo(
    () =>
      (usersData?.users || []).map((u: { id: string; firstName: string; lastName: string; email: string }) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`.trim() || u.email,
      })),
    [usersData]
  );

  const deleteOfferMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/offers/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      toast.success('Offer deleted successfully');
    },
    onError: (err: unknown) => {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(message || 'Failed to delete offer');
    },
  });

  const handleDelete = (id: string, offerName: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete offer "${offerName}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50',
        },
        {
          label: 'Delete',
          onClick: () => deleteOfferMutation.mutate(id),
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700',
        },
      ],
    });
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'offerType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={offerTypeOptions}
              value={filterOfferType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ offerType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={STATUS_FILTER_OPTIONS}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ inactive: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'createdBy':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={createdByOptions}
              value={filterCreatedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ createdById: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterName, filterOfferType, filterStatus, filterCreatedBy, offerTypeOptions, createdByOptions, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: appliedSearch, color: 'gray' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Offer', value: filterName, color: 'blue' });
    }
    if (filterOfferType) {
      chips.push({
        id: 'offerType',
        label: 'Type',
        value: filterOfferType.replace('_', ' '),
        color: 'teal',
      });
    }
    if (filterStatus) {
      const label = STATUS_FILTER_OPTIONS.find((o) => o.value === filterStatus)?.label || filterStatus;
      chips.push({ id: 'inactive', label: 'Status', value: label, color: 'green' });
    }
    if (filterCreatedBy) {
      const creator = createdByOptions.find((o: { value: string; label: string }) => o.value === filterCreatedBy);
      chips.push({ id: 'createdById', label: 'Created By', value: creator?.label || 'Unknown', color: 'purple' });
    }
    return chips;
  }, [appliedSearch, filterName, filterOfferType, filterStatus, filterCreatedBy, createdByOptions]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'offerType':
        patchSearchParams({ offerType: null, page: '1' });
        break;
      case 'inactive':
        patchSearchParams({ inactive: null, page: '1' });
        break;
      case 'createdById':
        patchSearchParams({ createdById: null, page: '1' });
        break;
    }
  };

  const clearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      offerType: null,
      inactive: null,
      createdById: null,
      page: '1',
    });
  };

  const getOfferTypeLabel = (type: string) => type.replace('_', ' ');

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading offers</div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Offers</h1>
              <p className="text-xs text-gray-500">Manage service offers and their configurations</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search offers..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Link
                  to="/offers/new"
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Add Offer
                </Link>
              )}
            </div>
          </div>
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={clearAllFilters}
              />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('name', 'Offer')}
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('offerType', 'Type')}
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('status', 'Status')}
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('createdBy', 'Created By')}
                    </th>
                    <th className="bg-gray-50 relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.offers?.map((offer) => (
                    <tr key={offer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{offer.name}</div>
                          {offer.description && (
                            <div className="text-sm text-gray-500 mt-1 max-w-xs truncate">{offer.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {getOfferTypeLabel(offer.offerType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            offer.inactive ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {offer.inactive ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            {offer.User_Offer_createdByIdToUser?.displayName ||
                              `${offer.User_Offer_createdByIdToUser?.firstName || ''} ${offer.User_Offer_createdByIdToUser?.lastName || ''}`.trim()}
                          </div>
                          {offer.User_Offer_createdByIdToUser?.email && (
                            <div className="text-gray-500">{offer.User_Offer_createdByIdToUser.email}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          <Link to={`/offers/${offer.id}`} className="text-blue-600 hover:text-blue-900" title="View Details">
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          {(user?.role === 'admin' || user?.role === 'super_admin') && (
                            <Link to={`/offers/${offer.id}/edit`} className="text-yellow-600 hover:text-yellow-900" title="Edit Offer">
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          )}
                          {(user?.role === 'admin' || user?.role === 'super_admin') && (
                            <button
                              onClick={() => handleDelete(offer.id, offer.name)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Offer"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data && data.pagination.total > 0 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-700">
                      Show
                    </label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => patchSearchParams({ pageSize: e.target.value, page: '1' })}
                      className="border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">entries</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * pageSize, data.pagination.total)}</span> of{' '}
                    <span className="font-medium">{data.pagination.total}</span> entries
                  </div>
                </div>
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => patchSearchParams({ page: '1' })}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    onClick={() => patchSearchParams({ page: String(page - 1) })}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span className="px-2 text-sm text-gray-700">
                    Page {page} of {data.pagination.totalPages}
                  </span>
                  <button
                    onClick={() => patchSearchParams({ page: String(page + 1) })}
                    disabled={page === data.pagination.totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => patchSearchParams({ page: String(data.pagination.totalPages) })}
                    disabled={page === data.pagination.totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Last
                  </button>
                </nav>
              </div>
            )}

            {data?.offers?.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No offers</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {activeFilterChips.length > 0 ? 'No offers match your filters.' : 'Get started by creating a new offer.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OfferList;
